import React, { useState } from 'react';
import styled from 'styled-components';
import { Link, useNavigate } from 'react-router-dom';
import { ForgotPasswordForm } from '../../../modules/auth/components/ForgotPasswordForm';
import { ResetPasswordForm } from '../../../modules/auth/components/ResetPasswordForm';
import { RecoveryEmailForm, CONTATO_SSI } from '../../../modules/auth/components/RecoveryEmailForm';
import { resetSenhaService } from '../../../modules/auth/services/passwordService';
import { Alert } from '../../../components/ui/Alert/Alert';
import { colors, breakpoints } from '../../../assets/styles/GlobalStyles';
import LOGO from '../../../assets/images/logo-oabms.png';
import { rolarParaTopo } from '../../../utils/rolarParaTopo';

const PageContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, ${colors.backgroundLight} 0%, #e2e8f0 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  font-family: 'Arial', sans-serif;

  @media (max-width: ${breakpoints.mobile}) {
    padding: 10px;
  }
`;

const Card = styled.div`
  max-width: 480px;
  width: 100%;
  background: ${colors.white};
  border-radius: 24px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  overflow: hidden;

  @media (max-width: ${breakpoints.mobile}) {
    border-radius: 20px;
  }
`;

const Header = styled.div`
  background: linear-gradient(135deg, ${colors.darkBlue} 0%, ${colors.blueMedium} 100%);
  padding: 2.5rem 2rem;
  text-align: center;
  color: ${colors.white};

  @media (max-width: ${breakpoints.mobile}) {
    padding: 2rem 1.5rem;
  }
`;

const LogoImage = styled.img`
  max-width: 120px;
  height: auto;
  margin-bottom: 1rem;
  filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.2));

  @media (max-width: ${breakpoints.mobile}) {
    max-width: 100px;
  }
`;

const Title = styled.h1`
  font-size: 1.8rem;
  font-weight: 700;
  margin: 0 0 0.5rem 0;
  color: ${colors.white};

  @media (max-width: ${breakpoints.mobile}) {
    font-size: 1.5rem;
  }
`;

const Subtitle = styled.p`
  font-size: 1rem;
  opacity: 0.9;
  margin: 0;
  color: ${colors.white};

  @media (max-width: ${breakpoints.mobile}) {
    font-size: 0.9rem;
  }
`;

const Content = styled.div`
  padding: 2.5rem 2rem;
  background: ${colors.white};

  @media (max-width: ${breakpoints.mobile}) {
    padding: 1.5rem;
  }
`;

const BackLink = styled(Link)`
  display: inline-block;
  margin-top: 1.5rem;
  color: ${colors.darkBlue};
  text-decoration: none;
  font-size: 0.9rem;
  font-weight: 500;
  transition: all 0.2s ease;

  &:hover {
    color: ${colors.lightBlue};
    text-decoration: underline;
  }

  @media (max-width: ${breakpoints.mobile}) {
    font-size: 0.85rem;
  }
`;

const BackButton = styled.button`
  display: inline-block;
  margin-top: 1rem;
  background: none;
  border: none;
  padding: 0;
  color: ${colors.darkBlue};
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    color: ${colors.lightBlue};
    text-decoration: underline;
  }

  @media (max-width: ${breakpoints.mobile}) {
    font-size: 0.85rem;
  }
`;

const SuccessMessage = styled.div`
  text-align: center;

  p {
    color: ${colors.textMuted};
    line-height: 1.6;
    margin-bottom: 2rem;

    strong {
      color: ${colors.darkBlue};
    }
  }

  button {
    background: none;
    border: none;
    color: ${colors.darkBlue};
    text-decoration: underline;
    cursor: pointer;
    font-size: 0.9rem;
    transition: all 0.2s ease;

    &:hover {
      color: ${colors.lightBlue};
    }
  }
`;

/**
 * Página de recuperação de senha, em 2 passos na mesma tela:
 * 1. 'solicitar' - usuário informa CPF/CNPJ, API envia código de 6 dígitos por e-mail
 * 2. 'confirmar' - usuário informa o código + a nova senha
 * Não depende mais de link enviado por e-mail (a API mudou para código manual).
 *
 * Sem e-mail de recuperação cadastrado, ou sem acesso a ele, o usuário passa
 * pelo passo 'trocarEmail' (perguntas sobre o cadastro). Ao concluir, o código
 * já é solicitado para o novo e-mail e a tela segue para 'confirmar'.
 * Errar as perguntas até o limite desativa o login: passo 'bloqueado'.
 */
export const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState('solicitar'); // 'solicitar' | 'confirmar' | 'trocarEmail' | 'bloqueado' | 'sucesso'
  const [documento, setDocumento] = useState('');
  const [emailMascarado, setEmailMascarado] = useState('');
  const [alert, setAlert] = useState(null);

  const handleSolicitarSuccess = (documentoEnviado, data) => {
    setDocumento(documentoEnviado);
    setEmailMascarado(data?.email || '');
    setStep('confirmar');
    setAlert({
      type: 'success',
      message: `Enviamos um código de verificação para ${data?.email || 'o e-mail cadastrado'}.`
    });
  };

  const handleSolicitarError = (error, documentoEnviado) => {
    if (error.type === 'no_recovery_email') {
      setDocumento(documentoEnviado);
      setStep('trocarEmail');
      setAlert({
        type: 'warning',
        message: 'Seu cadastro ainda não tem e-mail de recuperação. Cadastre um abaixo para receber o código de redefinição de senha.'
      });
      rolarParaTopo();
      return;
    }

    setAlert({
      type: 'error',
      message: error.message || 'Erro ao processar solicitação'
    });
    rolarParaTopo();
  };

  const handleConfirmarSuccess = () => {
    setStep('sucesso');
    setAlert({
      type: 'success',
      message: 'Senha redefinida com sucesso! Redirecionando para o login...'
    });
    setTimeout(() => navigate('/login'), 3000);
  };

  const handleConfirmarError = (error) => {
    setAlert({
      type: 'error',
      message: error.message || 'Erro ao redefinir senha'
    });
  };

  const voltarParaSolicitar = () => {
    setStep('solicitar');
    setAlert(null);
  };

  const abrirTrocaEmail = (documentoAtual) => {
    if (documentoAtual !== undefined) setDocumento(documentoAtual);
    setStep('trocarEmail');
    setAlert(null);
  };

  // E-mail trocado: já pede o código para o novo e-mail, sem o usuário
  // precisar digitar o CPF/CNPJ de novo
  const handleTrocaEmailSuccess = async (documentoEnviado, data) => {
    setDocumento(documentoEnviado);

    try {
      const resultado = await resetSenhaService.solicitarReset(documentoEnviado);
      const email = resultado.data?.email || data?.email;
      setEmailMascarado(email || '');
      setStep('confirmar');
      setAlert({
        type: 'success',
        message: `E-mail de recuperação atualizado. Enviamos um código de verificação para ${email || 'o novo e-mail'}.`
      });
    } catch (error) {
      setStep('solicitar');
      setAlert({
        type: 'warning',
        message: `E-mail de recuperação atualizado para ${data?.email || 'o novo e-mail'}, mas não foi possível enviar o código (${error.message}). Tente solicitar novamente.`
      });
    }
    rolarParaTopo();
  };

  // null limpa o alerta (ex: ao avançar para as perguntas)
  const handleTrocaEmailError = (error) => {
    if (!error) {
      setAlert(null);
      return;
    }
    setAlert({
      type: 'error',
      message: error.message || 'Erro ao alterar o e-mail de recuperação'
    });
    rolarParaTopo();
  };

  const handleBloqueado = (error) => {
    setStep('bloqueado');
    setAlert({ type: 'error', message: error.message });
    rolarParaTopo();
  };

  const titulos = {
    solicitar: { title: 'Recuperar Senha', subtitle: 'Digite seu CPF/CNPJ para receber um código' },
    confirmar: { title: 'Digite o código', subtitle: `Enviado para ${emailMascarado || 'seu e-mail'}` },
    trocarEmail: { title: 'E-mail de Recuperação', subtitle: 'Cadastre ou troque o e-mail que recebe o código' },
    bloqueado: { title: 'Acesso bloqueado', subtitle: 'Procure a OAB/MS para reativar seu acesso' },
    sucesso: { title: 'Tudo certo!', subtitle: 'Senha redefinida com sucesso' }
  };

  return (
    <PageContainer>
      <Card>
        <Header>
          <LogoImage src={LOGO} alt="OABMS" />
          <Title>{titulos[step].title}</Title>
          <Subtitle>{titulos[step].subtitle}</Subtitle>
        </Header>

        <Content>
          {alert && (
            <Alert
              type={alert.type}
              message={alert.message}
              onClose={step === 'sucesso' || step === 'bloqueado' ? undefined : () => setAlert(null)}
            />
          )}

          {step === 'solicitar' && (
            <ForgotPasswordForm
              documentoInicial={documento}
              onSuccess={handleSolicitarSuccess}
              onError={handleSolicitarError}
              onTrocarEmail={abrirTrocaEmail}
            />
          )}

          {step === 'confirmar' && (
            <ResetPasswordForm
              documento={documento}
              onSuccess={handleConfirmarSuccess}
              onError={handleConfirmarError}
              onVoltar={voltarParaSolicitar}
              onTrocarEmail={() => abrirTrocaEmail()}
            />
          )}

          {step === 'trocarEmail' && (
            <>
              <RecoveryEmailForm
                documentoInicial={documento}
                onSuccess={handleTrocaEmailSuccess}
                onBlocked={handleBloqueado}
                onError={handleTrocaEmailError}
              />
              <div style={{ textAlign: 'center' }}>
                <BackButton type="button" onClick={voltarParaSolicitar}>
                  ← Voltar para a recuperação de senha
                </BackButton>
              </div>
            </>
          )}

          {step === 'bloqueado' && (
            <SuccessMessage>
              <p>
                Por segurança, seu acesso foi <strong>bloqueado</strong> após
                respostas incorretas. Entre em contato com a <strong>OAB/MS</strong> para
                reativá-lo.
              </p>
              <p>{CONTATO_SSI}</p>
            </SuccessMessage>
          )}

          {step === 'sucesso' && (
            <SuccessMessage>
              <p>Sua senha foi redefinida. Você já pode entrar com a nova senha.</p>
            </SuccessMessage>
          )}

          {step !== 'sucesso' && (
            <div style={{ textAlign: 'center' }}>
              <BackLink to="/login">
                ← Voltar para o login
              </BackLink>
            </div>
          )}
        </Content>
      </Card>
    </PageContainer>
  );
};
